import Papa from 'papaparse';
import { DayColumn, DeliveryCard } from '../types/wizard';

/**
 * CSV row structure with core required fields and optional fields
 */
interface LibraryCsvRow {
  // Core required fields
  id: string;
  ordem: string;
  item: string;
  centro: string;
  cli_cod_pai: string;
  cli_razao_social: string;
  no_vendedor: string;
  cd_produto: string;
  prd_dc_linha: string;
  prd_espessura: string;
  prd_tx_breve: string;
  qt_vol_m3_liquido_gerencial: string;
  dt_emissao: string;
  dt_producao: string;
  
  // Optional fields (may not be present in all CSV formats)
  email_vendedor?: string;
  prd_largura?: string;
  prd_comprimento?: string;
  dt_preparacao_material?: string;
  dt_desejada_remessa?: string;
  request_uuid?: string;
  prd_confirmed?: string;
}

/**
 * Required headers that must be present (core fields)
 */
const CORE_REQUIRED_HEADERS = [
  'id',
  'ordem', 
  'item',
  'centro',
  'cli_cod_pai',
  'cli_razao_social',
  'no_vendedor',
  'cd_produto',
  'prd_dc_linha',
  'prd_espessura',
  'prd_tx_breve',
  'qt_vol_m3_liquido_gerencial',
  'dt_emissao',
  'dt_producao'
];

/**
 * All possible headers (20 columns - original full format)
 */
const ALL_POSSIBLE_HEADERS = [
  'id',
  'ordem', 
  'item',
  'email_vendedor', // Optional
  'centro',
  'cli_cod_pai',
  'cli_razao_social',
  'no_vendedor',
  'cd_produto',
  'prd_dc_linha',
  'prd_espessura',
  'prd_largura', // Optional
  'prd_comprimento', // Optional
  'prd_tx_breve',
  'qt_vol_m3_liquido_gerencial',
  'dt_emissao',
  'dt_preparacao_material', // Optional
  'dt_desejada_remessa', // Optional  
  'dt_producao',
  'request_uuid', // Optional
  'prd_confirmed' // Optional
];

/**
 * Parse CSV string to DayColumn array for SalesStackedSchedule
 * Validates core required headers (flexible column count)
 * @param csv - CSV string from API response
 * @returns Array of DayColumn objects
 */
export const parseCsvToDayColumns = (csv: string): DayColumn[] => {
  try {
    // Validate CSV structure first
    if (!validateCsvStructure(csv)) {
      throw new Error('Formato de CSV inválido — verifique cabeçalhos e tente novamente.');
    }

    // Parse CSV with Papa Parse
    const { data, errors } = Papa.parse<LibraryCsvRow>(csv, {
      header: true,
      skipEmptyLines: true,
    });

    if (errors.length > 0) {
      console.warn('CSV parsing warnings:', errors);
    }

    // Group rows by production date (dt_producao)
    const dayMap = new Map<string, DeliveryCard[]>();

    data.forEach((row: LibraryCsvRow, index: number) => {
      // Validate essential fields only (some fields may be empty in real data)
      if (!row.id) {
        console.warn(`Skipping invalid row ${index + 1}:`, row);
        return;
      }

      // Parse numeric values with fallbacks and round to 2 decimals
              const volume = Math.round((parseFloat(row.qt_vol_m3_liquido_gerencial) || 0) * 100) / 100;
        const espessura = parseFloat(row.prd_espessura) || 0;
        const largura = parseFloat(row.prd_largura || '0') || 0;
        const comprimento = parseFloat(row.prd_comprimento || '0') || 0;

      // Create delivery card compatible with wizard DeliveryCard type
      const deliveryCard: DeliveryCard = {
        id: row.id.trim(),
        cli_cod_pai: parseInt(row.cli_cod_pai) || 0,
        cli_razao_social: row.cli_razao_social?.trim() || '',
        vendedor: row.no_vendedor?.trim() || '',
        email_vendedor: row.email_vendedor?.trim() || 'Não informado',
        units: volume, // Volume in m³
        prd_espessura: espessura,
        dt_producao: row.dt_producao && row.dt_producao.trim() ? row.dt_producao.trim() : '',
        
        // Additional fields for backward compatibility and display
        cliente: row.cli_razao_social?.trim() || '',
        produto: row.prd_tx_breve?.trim() || '',
        volume: volume,
        espessura: espessura,
        largura: largura, // New field for width filtering
        comprimento: comprimento, // New field for length
        
        // Store all original fields for export (handle empty strings and optional fields)
        ordem: row.ordem || '',
        item: row.item || '',
        centro: row.centro || '',
        cd_produto: row.cd_produto || '',
        prd_dc_linha: row.prd_dc_linha || '',
        prd_largura: row.prd_largura || '',
        prd_comprimento: row.prd_comprimento || '',
        dt_emissao: row.dt_emissao || '',
        dt_preparacao_material: row.dt_preparacao_material || row.dt_desejada_remessa || '',
        request_uuid: row.request_uuid || '',
        prd_confirmed: row.prd_confirmed === 'True' || row.prd_confirmed === 'true',
      };

      // Group by production date (dt_producao)
      if (deliveryCard.dt_producao && deliveryCard.dt_producao !== '') {
        const dateKey = deliveryCard.dt_producao.split('T')[0]; // Get just the date part
        
        if (!dayMap.has(dateKey)) {
          dayMap.set(dateKey, []);
        }
        dayMap.get(dateKey)!.push(deliveryCard);
      } else {
        // If no production date, add to unprioritized date (will be filtered later)
        console.log(`Card ${row.id} has no production date - will be available as unprioritized`);
        
        // Use a special date key for unprioritized orders
        const unprioritizedKey = 'unprioritized';
        if (!dayMap.has(unprioritizedKey)) {
          dayMap.set(unprioritizedKey, []);
        }
        dayMap.get(unprioritizedKey)!.push(deliveryCard);
      }
    });

    // Convert map to DayColumn array and sort by date
    // Filter out unprioritized orders from schedule (they'll be handled separately)
    const dayColumns: DayColumn[] = Array.from(dayMap.entries())
      .filter(([date]) => date !== 'unprioritized') // Exclude unprioritized orders from schedule
      .map(([date, deliveries]) => ({
        date,
        deliveries: deliveries.sort((a, b) => b.volume - a.volume), // Sort by volume descending
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Also add unprioritized orders as a separate day for processing
    const unprioritizedOrders = dayMap.get('unprioritized');
    if (unprioritizedOrders && unprioritizedOrders.length > 0) {
      dayColumns.push({
        date: '', // Empty date for unprioritized orders
        deliveries: unprioritizedOrders.sort((a, b) => b.volume - a.volume)
      });
    }

    console.log('Parsed CSV production dates:', Array.from(dayMap.keys()).sort());
    console.log('Day columns created:', dayColumns.map(d => ({ date: d.date, count: d.deliveries.length })));

    return dayColumns;
  } catch (error) {
    console.error('Failed to parse CSV:', error);
    if (error instanceof Error) {
      throw error; // Re-throw with original message
    }
    throw new Error('Falha ao processar dados da priorização');
  }
};

/**
 * Alternative function name for backward compatibility
 */
export const parseCsvToScheduleData = parseCsvToDayColumns;

/**
 * Validate CSV structure - check for required core headers (flexible column count)
 * @param csv - CSV string to validate
 * @returns True if CSV has expected structure
 */
export const validateCsvStructure = (csv: string): boolean => {
  try {
    const lines = csv.trim().split('\n');
    if (lines.length < 2) {
      console.error('CSV validation failed: Need at least header + one data row');
      return false;
    }

    const headerLine = lines[0].trim();
    const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
    
    // Check minimum number of columns (must have core required fields)
    if (headers.length < CORE_REQUIRED_HEADERS.length) {
      console.error(`CSV validation failed: Expected at least ${CORE_REQUIRED_HEADERS.length} columns, got ${headers.length}`);
      return false;
    }
    
    // Check that all core required headers are present
    const coreHeadersLower = CORE_REQUIRED_HEADERS.map(h => h.toLowerCase());
    const missingHeaders = coreHeadersLower.filter(reqHeader => 
      !headers.includes(reqHeader)
    );
    
    if (missingHeaders.length > 0) {
      console.error(`CSV validation failed: Missing required headers: ${missingHeaders.join(', ')}`);
      return false;
    }
    
    // Check that all present headers are valid (known headers)
    const allValidHeadersLower = ALL_POSSIBLE_HEADERS.map(h => h.toLowerCase());
    const invalidHeaders = headers.filter(header => 
      !allValidHeadersLower.includes(header)
    );
    
    if (invalidHeaders.length > 0) {
      console.warn(`CSV validation warning: Unknown headers found: ${invalidHeaders.join(', ')}`);
      // Don't fail validation for unknown headers, just warn
    }
    
    console.log(`✅ CSV structure validation passed (${headers.length} columns)`);
    return true;
  } catch (error) {
    console.error('CSV validation error:', error);
    return false;
  }
};

/**
 * Get summary statistics from parsed day columns
 * @param dayColumns - Array of DayColumn objects
 * @returns Summary object with totals and counts
 */
export const getCsvSummary = (dayColumns: DayColumn[]) => {
  const totalDays = dayColumns.length;
  const totalDeliveries = dayColumns.reduce((sum, day) => sum + day.deliveries.length, 0);
  const totalVolume = dayColumns.reduce((sum, day) => 
    sum + day.deliveries.reduce((daySum, delivery) => daySum + delivery.volume, 0), 0
  );
  const totalUnits = dayColumns.reduce((sum, day) => 
    sum + day.deliveries.reduce((daySum, delivery) => daySum + delivery.units, 0), 0
  );

  const uniqueClients = new Set(
    dayColumns.flatMap(day => day.deliveries.map(d => d.cliente))
  ).size;

  const uniqueVendedores = new Set(
    dayColumns.flatMap(day => day.deliveries.map(d => d.vendedor).filter(Boolean))
  ).size;

  return {
    totalDays,
    totalDeliveries,
    totalVolume: Math.round(totalVolume * 100) / 100,
    totalUnits: Math.round(totalUnits * 100) / 100,
    uniqueClients,
    uniqueVendedores,
  };
}; 